import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Import all components
import AccountOverview from "./components/AccountOverview";
import ApiKeysManager from "./components/ApiKeysManager";
import AccessTokensManager from "./components/AccessTokensManager";
import UsageAnalytics from "./components/UsageAnalytics";
import ProjectManager from "./components/ProjectManager";
import IntegrationStatus from "./components/IntegrationStatus";
import QuickActions from "./components/QuickActions";
import BillingSection from "./components/BillingSection";
import OnboardingSection from "./components/OnboardingSection";
import GettingStarted from "./components/GettingStarted";
import UserInfoModal from "./components/UserInfoModal";
import PlanManagement from "./components/PlanManagement";
import ManageQuota from "./components/ManageQuota";

const DeveloperPortal = () => {
  const { user, userProfile, signOut } = useAuth();
  const { t } = useTranslation('developer-portal');
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // Handle navigation state for setting active tab
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  const isTrue = (v) => v === true || v === 'true' || v === 1;
  const emailVerified = (() => {
    const src = userProfile || user || {};
    return (
      isTrue(src.email_verified) ||
      isTrue(src.emailVerified) ||
      isTrue(src.is_email_verified) ||
      isTrue(src.isEmailVerified) ||
      isTrue(src.is_verified) ||
      isTrue(src.isVerified)
    );
  })();
  const phoneVerified = (() => {
    const src = userProfile || {};
    return (
      isTrue(src.phone_verified) ||
      isTrue(src.phoneVerified) ||
      isTrue(src.is_phone_verified) ||
      isTrue(src.isPhoneVerified) ||
      isTrue(src.is_verified) ||
      isTrue(src.isVerified)
    );
  })();

  const navigationTabs = [
    { id: "overview", label: t("portal.overview"), icon: "LayoutDashboard" },
    { id: "projects", label: t("portal.projects"), icon: "FolderOpen" },
    { id: "access-tokens", label: t("portal.access_tokens"), icon: "Shield" },
    { id: "analytics", label: t("portal.analytics"), icon: "BarChart3" },
    { id: "integrations", label: t("portal.integrations"), icon: "Plug" },
    { id: "manage-quota", label: t("portal.manage_quota"), icon: "Gauge" },
    { id: "plans-billing", label: t("portal.plans_billing"), icon: "CreditCard" },
    { id: "billing", label: t("portal.billing"), icon: "FileText" },
    { id: "getting-started", label: t("portal.getting_started"), icon: "Rocket" },
  ];

  const renderActiveContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-8">
            <AccountOverview />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <QuickActions />
              <OnboardingSection />
            </div>
          </div>
        );
      case "access-tokens":
        return <AccessTokensManager />;
      case "analytics":
        return <UsageAnalytics />;
      case "projects":
        return <ProjectManager />;
      case "manage-quota":
        return <ManageQuota />;
      case "integrations":
        return <IntegrationStatus />;
      case "plans-billing":
        return <PlanManagement />;
      case "billing":
        return <BillingSection />;
      case "getting-started":
        return <GettingStarted />;
      default:
        return (
          <div className="space-y-8">
            <AccountOverview />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <QuickActions />
              <OnboardingSection />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="auto">
      <Header />
      <div className="pt-16">
        <div className="flex">
          {/* Sidebar */}
          <div
            className={`${sidebarCollapsed ? "w-16" : "w-64"} h-screen transition-all duration-300 bg-card border-r border-border flex-shrink-0`}
            dir="auto"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-6">
                {!sidebarCollapsed && (
                  <h1 className="text-lg font-semibold text-foreground">
                    {t("portal.title")}
                  </h1>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="h-8 w-8"
                >
                  <Icon
                    name={sidebarCollapsed ? "ChevronRight" : "ChevronLeft"}
                    size={16}
                  />
                </Button>
              </div>

              {/* User Profile Section */}
              <div className="mb-6 p-3 border border-border rounded-lg bg-muted/30">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={16} color="white" />
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {userProfile?.full_name ||
                          user?.email?.split("@")[0] ||
                          "Developer"}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email || "Not available"}
                      </p>
                    </div>
                  )}
                  {!sidebarCollapsed ? (
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setIsUserModalOpen(true)}
                        title="Settings"
                      >
                        <Icon name="Settings" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                        onClick={async () => {
                          const { error } = await signOut();
                          if (!error) navigate("/");
                        }}
                        title="Sign out"
                      >
                        <Icon name="LogOut" size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className="hidden"></div>
                  )}
                </div>
              </div>

              {/* Navigation Items */}
              <nav className="space-y-2">
                {navigationTabs?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => setActiveTab(item?.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === item?.id
                        ? 'text-primary bg-primary/10 border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                    dir="auto"
                  >
                    <Icon name={item?.icon} size={16} />
                    {!sidebarCollapsed && (
                      <span className="text-left">{item?.label}</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-hidden">
            <div className="p-6 lg:p-8" dir="auto">
              {/* Page Header */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {navigationTabs?.find(tab => tab.id === activeTab)?.label || t("portal.overview")}
                </h2>
                <p className="text-muted-foreground">
                  {activeTab === "overview" && t("account_overview.welcome_back")}
                  {activeTab === "integrations" && "Monitor integration status and configure webhooks."}
                  {activeTab === "plans-billing" && "Manage your subscription plan details."}
                  {activeTab === "billing" && "View billing information, usage limits, and invoice history."}
                  {activeTab === "getting-started" && "Follow these steps to get your integration up and running."}
                </p>
                
                <div className="flex items-center space-x-3">
                  {(!emailVerified || !phoneVerified) && (
                    <div className="flex items-center space-x-3 px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                      <div className="flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={16} />
                        <span className="text-sm font-medium">Verification required</span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs">
                        {!emailVerified && (
                          <div className="inline-flex items-center px-2 py-0.5 rounded border border-amber-300">
                            <Icon name="Mail" size={12} className="mr-1" />
                            Email
                          </div>
                        )}
                        {!phoneVerified && (
                          <div className="inline-flex items-center px-2 py-0.5 rounded border border-amber-300">
                            <Icon name="Phone" size={12} className="mr-1" />
                            Phone
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => setIsUserModalOpen(true)}
                      >
                        Verify now
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Dynamic Content */}
              <div className="pb-8">{renderActiveContent()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* User Info Modal */}
      <UserInfoModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
      />
    </div>
  );
};

export default DeveloperPortal;
